import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { useIsMobile } from '@/lib/useIsMobile'
import { formatDate } from '@/lib/utils'
import { Search, X, Repeat, Pause, Play, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { toast } from 'sonner'
import NewRecurringScheduleModal from '@/components/payments/NewRecurringScheduleModal'

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  active:    { label: 'Active',    color: 'var(--positive)',     bg: 'var(--positive-dim)' },
  paused:    { label: 'Paused',    color: 'var(--warning)',      bg: 'var(--warning-dim)' },
  finished:  { label: 'Finished',  color: 'var(--ink-tertiary)', bg: 'var(--border-subtle)' },
}

const FREQUENCY_LABELS: Record<string, string> = {
  weekly:    'Weekly',
  monthly:   'Monthly',
  quarterly: 'Quarterly',
  yearly:    'Yearly',
}

type Series = {
  id: string
  workspace_id: string
  contact_id: string
  company_id: string | null
  service_line_id: string | null
  title: string
  template: any
  frequency: 'weekly' | 'monthly' | 'quarterly' | 'yearly'
  start_date: string
  end_date: string | null
  end_after_count: number | null
  generated_count: number
  next_run_date: string
  send_mode: 'draft' | 'auto_send'
  status: 'active' | 'paused' | 'finished'
  created_at: string
  updated_at: string
}

export default function RecurringInvoices() {
  const { workspace } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isMobile = useIsMobile()
  const [search, setSearch] = useState('')
  const [searchParams] = useSearchParams()
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all')
  const [showNewModal, setShowNewModal] = useState(false)

  // Contacts for the "New schedule" modal's customer picker. Mirrors
  // Invoices.tsx's typeahead pattern; cached separately by query key suffix.
  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', 'typeahead-recurring', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, company_name')
        .eq('workspace_id', workspace.id)
        .order('first_name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: series = [], isLoading, error } = useQuery<Series[]>({
    queryKey: ['recurring-invoice-series', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase
        .from('recurring_invoice_series')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false })
      if (error) {
        // Surface the precise PostgREST error so a broken read can't
        // silently masquerade as an empty list (same pattern as the
        // invoice-payments embed fix in InvoiceDetail).
        console.error('[recurring-invoice-series] query failed', error)
        throw error
      }
      return (data as Series[]) ?? []
    },
    enabled: !!workspace?.id,
  })

  const metrics = useMemo(() => {
    const active = series.filter(s => s.status === 'active').length
    const paused = series.filter(s => s.status === 'paused').length
    const generated = series.reduce((sum, s) => sum + (s.generated_count || 0), 0)
    return { active, paused, generated, total: series.length }
  }, [series])

  const filtered = useMemo(() => {
    let list = [...series]
    if (statusFilter !== 'all') list = list.filter(s => s.status === statusFilter)
    if (search) {
      const q = search.toLowerCase()
      list = list.filter(s =>
        s.title?.toLowerCase().includes(q) ||
        s.template?.contact_name?.toLowerCase?.().includes(q) ||
        s.template?.company_name?.toLowerCase?.().includes(q)
      )
    }
    return list
  }, [series, statusFilter, search])

  const updateStatus = async (id: string, next: 'active' | 'paused') => {
    const { error } = await supabase
      .from('recurring_invoice_series')
      .update({ status: next })
      .eq('id', id)
    if (error) { toast.error(`Update failed: ${error.message}`); return }
    toast.success(next === 'paused' ? 'Schedule paused' : 'Schedule resumed')
    queryClient.invalidateQueries({ queryKey: ['recurring-invoice-series', workspace?.id] })
  }

  const deleteSeries = async (s: Series) => {
    const count = s.generated_count || 0
    const tail = count === 0
      ? 'No invoices have been generated yet.'
      : `The ${count} invoice${count === 1 ? '' : 's'} it already created will be kept.`
    // eslint-disable-next-line no-alert
    if (!confirm(`Delete this schedule? ${tail}`)) return
    const { error } = await supabase
      .from('recurring_invoice_series')
      .delete()
      .eq('id', s.id)
    if (error) { toast.error(`Delete failed: ${error.message}`); return }
    toast.success('Schedule deleted')
    queryClient.invalidateQueries({ queryKey: ['recurring-invoice-series', workspace?.id] })
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>
      <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Recurring schedules</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>{metrics.active} active</p>
          </div>
          <button onClick={() => setShowNewModal(true)} className="btn-primary">
            <Repeat style={{ width: 14, height: 14 }} /> New schedule
          </button>
        </div>

        {/* Metrics — auto-fit, same pattern as Invoices.tsx. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Active',          value: metrics.active,    color: 'var(--positive)',     icon: CheckCircle2 },
            { label: 'Paused',          value: metrics.paused,    color: 'var(--warning)',      icon: Pause },
            { label: 'Generated total', value: metrics.generated, color: 'var(--accent)',       icon: Repeat },
            { label: 'Schedules',       value: metrics.total,     color: 'var(--info)',         icon: AlertTriangle },
          ].map(m => (
            <div key={m.label} style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -20, right: -20, width: 60, height: 60, background: `radial-gradient(circle, color-mix(in srgb, ${m.color} var(--status-tint-fill), transparent) 0%, transparent 70%)`, pointerEvents: 'none' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{m.label}</span>
                <m.icon style={{ width: 14, height: 14, color: m.color }} />
              </div>
              <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>{m.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 280, minWidth: 200 }}>
            <Search style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search schedules..."
              style={{ width: '100%', padding: '9px 12px 9px 32px', background: 'var(--field-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor='var(--border-subtle)'} />
            {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 12, height: 12 }} /></button>}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['all', 'active', 'paused', 'finished'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: statusFilter === s ? 'var(--accent)' : 'var(--hover-bg)', color: statusFilter === s ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: statusFilter === s ? 'none' : '1px solid var(--border-subtle)' }}>
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 28px 28px' }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, overflow: 'hidden' }}>
          {!isMobile && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px 130px 110px 100px 140px', padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface)' }}>
              {['Schedule', 'Cadence', 'Next run', 'Generated', 'Status', ''].map((h, i) => (
                <div key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
              ))}
            </div>
          )}

          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-tertiary)', fontSize: 13 }}>Loading...</div>
          ) : error ? (
            <div style={{ padding: '40px 24px', textAlign: 'center' }}>
              <AlertTriangle style={{ width: 32, height: 32, color: 'var(--danger)', margin: '0 auto 10px', display: 'block' }} />
              <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--danger)', margin: '0 0 6px' }}>Failed to load schedules</p>
              <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, fontFamily: 'monospace' }}>{(error as Error).message}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '60px 24px', textAlign: 'center' }}>
              <Repeat style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
              <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>
                {search || statusFilter !== 'all' ? 'No schedules match your filters' : 'No recurring schedules yet'}
              </p>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
                {search || statusFilter !== 'all' ? 'Try adjusting your filters' : "Open any invoice and choose 'Make recurring' to start one."}
              </p>
            </div>
          ) : (
            filtered.map((s, i) => {
              const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.finished
              const isLast = i === filtered.length - 1
              const customerLine = s.template?.contact_name || '—'
              const companyLine = s.template?.company_name || null

              if (isMobile) {
                return (
                  <motion.div key={s.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                    onClick={() => navigate('/app/invoices/recurring/' + s.id)}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px', borderBottom: isLast ? 'none' : '1px solid var(--border-subtle)', cursor: 'pointer', transition: 'background 150ms ease' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</p>
                        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customerLine}{companyLine ? ` — ${companyLine}` : ''}</p>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: cfg.bg, color: cfg.color, textTransform: 'capitalize', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {cfg.label}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--ink-secondary)' }}>
                      <span>{FREQUENCY_LABELS[s.frequency] || s.frequency}</span>
                      <span>{s.status === 'finished' ? 'Finished' : `Next: ${formatDate(s.next_run_date, 'MMM d')}`}</span>
                      <span>{s.generated_count} generated</span>
                    </div>
                    <RowActions series={s} onPauseResume={updateStatus} onDelete={deleteSeries} />
                  </motion.div>
                )
              }

              return (
                <motion.div key={s.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                  onClick={() => navigate('/app/invoices/recurring/' + s.id)}
                  style={{ display: 'grid', gridTemplateColumns: '1fr 130px 130px 110px 100px 140px', padding: '13px 16px', borderBottom: i < filtered.length - 1 ? '1px solid var(--border-subtle)' : 'none', alignItems: 'center', gap: 8, cursor: 'pointer', transition: 'background 150ms ease' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</p>
                    <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customerLine}{companyLine ? ` — ${companyLine}` : ''}</p>
                  </div>
                  <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>{FREQUENCY_LABELS[s.frequency] || s.frequency}</span>
                  <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>
                    {s.status === 'finished' ? '—' : formatDate(s.next_run_date, 'MMM d, yyyy')}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>{s.generated_count}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: cfg.bg, color: cfg.color, textTransform: 'capitalize', whiteSpace: 'nowrap', justifySelf: 'start' }}>
                    {cfg.label}
                  </span>
                  <RowActions series={s} onPauseResume={updateStatus} onDelete={deleteSeries} />
                </motion.div>
              )
            })
          )}
        </div>
        {filtered.length > 0 && (
          <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '10px 4px 0' }}>
            Daily sweep runs at 00:15 UTC. Schedules show the next run date — invoices land within ~24h.
          </p>
        )}
      </div>

      <AnimatePresence>
        {showNewModal && (
          <NewRecurringScheduleModal
            onClose={() => setShowNewModal(false)}
            onCreated={(id) => {
              setShowNewModal(false)
              navigate('/app/invoices/recurring/' + id)
            }}
            contacts={contacts}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

function RowActions({
  series, onPauseResume, onDelete,
}: {
  series: Series
  onPauseResume: (id: string, next: 'active' | 'paused') => void | Promise<void>
  onDelete: (s: Series) => void | Promise<void>
}) {
  const btn: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px',
    background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)',
    borderRadius: 7, color: 'var(--ink-secondary)', fontSize: 11, fontWeight: 600,
    cursor: 'pointer',
  }
  const danger: React.CSSProperties = {
    ...btn,
    background: 'color-mix(in srgb, var(--danger) var(--status-tint-faint), transparent)',
    border: '1px solid var(--danger-border)',
    color: 'var(--danger)',
  }
  // Rows are now clickable (navigate to series detail). Each inline action
  // button MUST stopPropagation so clicking Pause/Resume/Delete doesn't
  // also trigger the row's navigation.
  return (
    <div style={{ display: 'flex', gap: 6, justifySelf: 'end' }} onClick={e => e.stopPropagation()}>
      {series.status === 'active' && (
        <button onClick={e => { e.stopPropagation(); onPauseResume(series.id, 'paused') }} style={btn} title="Pause">
          <Pause style={{ width: 12, height: 12 }} /> Pause
        </button>
      )}
      {series.status === 'paused' && (
        <button onClick={e => { e.stopPropagation(); onPauseResume(series.id, 'active') }} style={btn} title="Resume">
          <Play style={{ width: 12, height: 12 }} /> Resume
        </button>
      )}
      <button onClick={e => { e.stopPropagation(); onDelete(series) }} style={danger} title="Delete schedule">
        <Trash2 style={{ width: 12, height: 12 }} />
      </button>
    </div>
  )
}

